import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { WithAuth } from './HOC';

import CreateRoom from '@pages/createRoom';
import Rejoin from '@pages/rejoin';
import CreateWebinar from './webinar/createWebinar';
import UpdateWebinar from './webinar/updateWebinar';
import { RegistrationForm, RoomStatusScreen } from './pages';

import { OLoader } from './shared';
import { useMobile, useAppInit } from './hooks';
import {
  EmojiAnimationsProvider,
  MeetRoomContextProvider,
  SettingsContextProvider,
  WhiteBoardContextProvider,
  NotesContextProvider,
  AuthenticationContextProvider,
} from './contexts';
import { ThemeProvider } from '@src/contexts/ThemeContext';
import { VirtualBackgroundProvider } from '@src/providers';

import { EWebinarRoutes } from './constants';
import { EMeetingRoutes } from './constants/routes.constants';

import './styles.scss';
import './index.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';

// QueryClient instance
const queryClient = new QueryClient();

const MeetRoom = lazy(() => import('@pages/MeetRoom'));
const WebinarClosed = lazy(() => import('@pages/WebinarClosed'));
const WebinarJoin = lazy(() => import('@pages/Webinar/WebinarJoin'));
const WebinarAttendees = lazy(() => import('@pages/WebinarAttendees'));
const WebinarRegistrationStatus = lazy(() => import('@pages/Webinar/WebinarRegistrationStatus'));
const PageStatus = lazy(() => import('@pages/PageStatus/PageStatus'));
const WebinarFull = lazy(() => import('@pages/WebinarFull'));

const WebinarRegistrationFull = lazy(() => import('@pages/WebinarRegistrationFull'));

const NoiseValidationTest = lazy(() => import('./components/NoiseValidation/NoiseValidationTest'));

const App = () => {
  useMobile();
  useAppInit();

  return (
    <div className="App">
      <Router>
        <VirtualBackgroundProvider>
          <ThemeProvider>
            <AuthenticationContextProvider>
              <MeetRoomContextProvider>
                <EmojiAnimationsProvider>
                  <WhiteBoardContextProvider>
                    <NotesContextProvider>
                      <QueryClientProvider client={queryClient}>
                        <Routes>
                          <Route path={EMeetingRoutes.HOME} element={WithAuth(CreateRoom)} />
                          <Route
                            path={EMeetingRoutes.JOIN}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <SettingsContextProvider>
                                  <MeetRoom />
                                </SettingsContextProvider>
                              </Suspense>
                            }
                          />
                          <Route
                            path={EMeetingRoutes.REJOIN}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <Rejoin />
                              </Suspense>
                            }
                          />
                          <Route path={EMeetingRoutes.END} element={<RoomStatusScreen status="ended" />} />
                          <Route path={EMeetingRoutes.EXIT_ROOM} element={<RoomStatusScreen status="notFound" />} />
                          <Route
                            path={EMeetingRoutes.ROOM_FULL}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="full" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EMeetingRoutes.RESTRICTED_ACCESS}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="restricted" />
                              </Suspense>
                            }
                          />
                          {/* temporary paths for webinar status pages */}
                          <Route
                            path={EWebinarRoutes.SUCCESS}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarRegistrationStatus status="success" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.ALREADY_REGISTERED}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarRegistrationStatus status="alreadyRegistered" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.DECLINE_INVITATION}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarRegistrationStatus status="declineInvitation" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.CLOSED}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarClosed />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.FULL}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarFull />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.WEBINAR_FULL_REGISTRATION}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <WebinarRegistrationFull />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.WEBINAR_FULL_ROOM}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="full" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.WEBINAR_EXPIRED}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="invalid" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.RESTRICTED_ACCESS}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="restricted" />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.ROOM_NOT_FOUND}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <PageStatus status="notFound" />
                              </Suspense>
                            }
                          />
                          <Route path={EWebinarRoutes.CREATE} element={WithAuth(CreateWebinar)} />
                          <Route path={EWebinarRoutes.UPDATE} element={WithAuth(UpdateWebinar)} />
                          <Route path={EWebinarRoutes.REGISTRATION_FORM} element={<RegistrationForm />} />
                          <Route
                            path={EWebinarRoutes.ADMIN_WEBINAR}
                            element={<Suspense fallback={<OLoader />}>{WithAuth(WebinarAttendees)}</Suspense>}
                          />
                          <Route
                            path={EWebinarRoutes.JOIN}
                            element={
                              <SettingsContextProvider>
                                <Suspense fallback={<OLoader />}>
                                  <WebinarJoin />
                                </Suspense>
                              </SettingsContextProvider>
                            }
                          />
                          <Route
                            path={EWebinarRoutes.REJOIN}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <Rejoin />
                              </Suspense>
                            }
                          />
                          <Route
                            path={EMeetingRoutes.TEST_NOISE}
                            element={
                              <Suspense fallback={<OLoader />}>
                                <NoiseValidationTest />
                              </Suspense>
                            }
                          />
                        </Routes>
                      </QueryClientProvider>
                    </NotesContextProvider>
                  </WhiteBoardContextProvider>
                </EmojiAnimationsProvider>
              </MeetRoomContextProvider>
            </AuthenticationContextProvider>
          </ThemeProvider>
        </VirtualBackgroundProvider>
      </Router>
    </div>
  );
};

export default App;
